import React, { FormEvent, useState } from 'react';
import { useFormik } from 'formik';

import { CSSObjectWithLabel } from 'react-select';

import ModalFormDatepicker from '@/components/business/ModalForm/ModalFormDatepicker/ModalFormDatepicker';
import ModalFormDropdown from '@/components/business/ModalForm/ModalFormDropdown/ModalFormDropdown';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import ModalFormTextarea from '@/components/business/ModalForm/ModalFormTextarea/ModalFormTextarea';
import { isValidDate, toMutationDateString } from '@/utils/date.utils';
import { initialValues, validationSchema } from './validation';
import { PAUSE_BILLING_TYPE } from '@/config/reasonTypes.config';
import { PauseBillingParams } from '../../hooks/usePauseBilling';
import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';
import { FormattingUtils } from '@/utils/Formatting.utils';
import ModalFormTextInput from '@/components/business/ModalForm/ModalFormTextInput/ModalFormTextInput';

interface IPauseBillingModalProps {
  onClose: () => void;
  pauseBilling: (inputParams: PauseBillingParams) => void;
  reasonCodes: ReasonCode[];
  reasonTypes: ReasonType[];
  isLoadingReasonCodes?: boolean;
}

export default function PauseBillingModal({
  onClose,
  pauseBilling,
  reasonCodes,
  reasonTypes,
  isLoadingReasonCodes
}: IPauseBillingModalProps) {

  const [isWarningVisible, setIsWarningVisible] = useState<boolean>(false);
  const [isInvoiceMarked, setIsInvoiceMarked] = useState<boolean>(false);

  const { values, setFieldValue, errors, isValid, setFieldTouched, touched } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => undefined,
    validateOnMount: true,
  });

  const collectibleStatus = false; // TODO change to values.collectible when we add back coll/uncollectibles

  const onSubmitMainForm = (e: FormEvent) => {
    e.preventDefault();
    setIsWarningVisible(true);
  };

  const onPauseBilling = () => {
    pauseBilling({
      endDate: values.endDate ? toMutationDateString(values.endDate) : undefined,
      reasonCode: values.reasonCode ?? '',
      comment: values.comment,
      reference: values.reference,
      collectible: false,
    });
  };


  if (isWarningVisible) {
    return (
      <AgConfirmationModal
        isOpen={true}
        title="You are about to pause the billing for this contract"
        onClose={onClose}
        onConfirm={onPauseBilling}
        contentStyle={{ width: 680 }}
      >
        Pausing the billing means stopping all billing activities related to this Contract until it's resumed.
        <br />
        Use it only with extra precaution.
      </AgConfirmationModal>
    );
  }

  return (
    <>
      {isInvoiceMarked && (
        <AgConfirmationModal
          isOpen={true}
          title={`Pause Billing - Mark Invoices as ${collectibleStatus}`}
          onClose={() => {
            setIsInvoiceMarked(false);
            setFieldValue('collectible', null);
            setFieldTouched('collectible', false);
          }}
          onConfirm={() => {
            setIsInvoiceMarked(false);
          }}
          contentStyle={{ width: 680 }}
          closeOnConfirm={false}
        >
          You are about to mark the paused contract as {collectibleStatus}. < br />
          This means that sales orders will {collectibleStatus ? 'be ' : 'not be '}
          billed when billing is resumed. < br /> This change cannot be reverted.
        </AgConfirmationModal>
      )}
      <AgModalWindow
        title="Pause Billing"
        isOpen={true}
        onClose={onClose}
        contentStyle={{ width: 340 }}
      >
        <form onSubmit={onSubmitMainForm}>
          <div className="mb-15">
            <ModalFormDropdown<ReasonType>
              id="reasonType"
              labelTitle="Reason Type"
              optionsData={reasonTypes}
              getOptionLabel={(option) => option.formattedType}
              getOptionValue={(option) => option.type}
              disabled
              defaultValue={reasonTypes.find((el) => el.type === PAUSE_BILLING_TYPE)}
            />
          </div>
          <div className="mb-15 mt-10">
            <ModalFormDropdown<ReasonCode>
              id="reasonCode"
              labelTitle="Reason Name"
              requiredIcon={true}
              isSearchable={true}
              optionsData={reasonCodes}
              placeholder="Select or type to search"
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.code}
              selectedOption={reasonCodes.find((el) => el.code === values.reasonCode) ?? null}
              menuPortalTarget={document.querySelector('body')}
              customStyles={{
                menuList: (provided) => ({ ...provided, maxHeight: 200 } as CSSObjectWithLabel)
              }}
              handleChange={(newValue) => {
                setFieldTouched('reasonCode', true, false);
                setFieldValue('reasonCode', newValue?.code);
              }}
              validationMessage={
                (errors?.reasonCode && touched.reasonCode) ? errors.reasonCode : undefined
              }
              isLoading={isLoadingReasonCodes}
              disabled={isLoadingReasonCodes}
            />
          </div>
          <div className="mb-15">
            <ModalFormDatepicker
              id="startDate"
              labelTitle="Pause Billing Start Date"
              disabled={true}
              value={new Date()}
              onChange={() => { }}
            />
          </div>
          <div className="mb-15">
            <ModalFormDatepicker
              id="endDate"
              placeholder="endDate"
              labelTitle="Pause Billing End Date"
              requiredIcon={true}
              onChange={({ value }) => {
                setFieldTouched('endDate', true, false);
                setFieldValue('endDate', value);
              }}
              disabled={isLoadingReasonCodes}
              value={values.endDate && isValidDate(new Date(values.endDate)) ? new Date(values.endDate) : undefined}
              validationMessage={errors?.endDate && touched.endDate ? errors.endDate : undefined}
            />
          </div>
          <div className="mb-15">
            <ModalFormTextInput
              id="reference"
              labelTitle="Reference"
              placeholder='Enter reference'
              value={values.reference ?? undefined}
              onChange={(event) => {
                setFieldTouched('reference', true, false);
                setFieldValue('reference',
                  FormattingUtils.replaceControlCharacters(event.target.value)
                    ? event.target.value
                    : null);
              }}
            />
          </div>
          <div>
            <ModalFormTextarea
              id="comment"
              labelTitle="Comment"
              placeholder="Enter short comments"
              value={values.comment ?? undefined}
              onChange={(event) => {
                setFieldTouched('comment', true, false);
                setFieldValue('comment',
                  FormattingUtils.replaceControlCharacters(event.target.value)
                    ? event.target.value
                    : null);
              }}
              validationMessage={
                errors?.comment && touched.comment ? errors.comment : undefined
              }
            />
          </div>
          <ActionButtonsWrapper
            leftButton={(
              <Button
                buttonStyleType={ButtonStyleType.SECONDARY}
                onClick={onClose}
              >
                Cancel
              </Button>
            )}
            rightButton={(
              <Button
                disabled={!isValid}
                buttonStyleType={ButtonStyleType.PRIMARY}
                type="submit"
              >
                Save
              </Button>
            )}
          />
        </form>
      </AgModalWindow>
    </>
  );
}
